import React, { useCallback, useEffect, useRef, useState } from "react";

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

interface DateRangeRule {
  min?: number;
  max?: number;
}

interface IQuestionDateInput {
  placeholder?: string;
  value?: string;
  rules?: {
    day?: DateRangeRule;
    month?: DateRangeRule;
    year?: DateRangeRule;
  };
  errorMessage?: string;
  onSelect: (date: string) => any;
}

const QuestionDateInput = ({
  rules,
  placeholder,
  value,
  errorMessage,
  onSelect,
}: IQuestionDateInput) => {
  const [day, setDay] = useState(value?.split("-")[1] ?? "");
  const [month, setMonth] = useState(value?.split("-")[2] ?? "");
  const [year, setYear] = useState(value?.split("-")[0] ?? "");

  const dayRef = useRef<HTMLInputElement | null>(null);
  const monthRef = useRef<HTMLInputElement | null>(null);
  const yearRef = useRef<HTMLInputElement | null>(null);

  const [showInvalidDateAlert, setShowInvalidDateAlert] = useState(false);
  const [isDateValid, setIsDateValid] = useState(false);

  const validateDay = useCallback(
    (value: string) => {
      if (!value || !/^0?[0-9]{1,2}$/.test(value)) {
        return false;
      }

      const dayNumber = parseInt(value, 10);

      if (
        dayNumber < (rules?.day?.min ?? 1) ||
        dayNumber > (rules?.day?.max ?? 31)
      ) {
        return false;
      }

      if (dayNumber === 29) {
        const year = new Date().getFullYear();
        if (!isLeapYear(year)) {
          return false;
        }
      }

      return true;
    },
    [rules?.day?.max, rules?.day?.min]
  );

  const validateMonth = useCallback(
    (value: string) => {
      if (!value || !/^0?[0-9]{1,2}$/.test(value)) {
        return false;
      }

      const monthNumber = parseInt(value);

      if (
        isNaN(monthNumber) ||
        monthNumber < (rules?.month?.min ?? 1) ||
        monthNumber > (rules?.month?.max ?? 12)
      ) {
        return false;
      }
      return true;
    },
    [rules?.month?.max, rules?.month?.min]
  );

  const validateYear = useCallback(
    (value: string) => {
      if (!value || !/^0?[0-9]{1,4}$/.test(value)) {
        return false;
      }

      const yearNumber = parseInt(value);
      const minYear = rules?.year?.min !== undefined ? rules.year.min : 1900;
      const maxYear =
        rules?.year?.max !== undefined
          ? rules.year.max
          : new Date().getFullYear() + 100;

      if (isNaN(yearNumber) || yearNumber < minYear || yearNumber > maxYear) {
        return false;
      }
      return true;
    },
    [rules?.year?.max, rules?.year?.min]
  );

  const handleDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value.length <= 1) {
      setDay(value);
    }

    if (value.length === 2) {
      const isValid = validateDay(value);
      if (isValid) {
        setDay(value);
        // document.getElementById("month-input")?.focus();
        if (monthRef.current) monthRef.current.focus();
      }
    } else {
      setDay(value);
    }
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value.length <= 1) {
      setMonth(value);
    }

    if (value.length === 2) {
      const isValid = validateMonth(value);
      if (isValid) {
        setMonth(value);
        // document.getElementById("year-input")?.focus();
        if (yearRef.current) yearRef.current.focus();
      }
    } else {
      setMonth(value);
    }

    if (value.length === 0 && dayRef.current) dayRef.current.focus();
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value.length <= 4) {
      setYear(value);
    }

    if (value.length === 4) {
      const isValid = validateYear(value);
      if (isValid) {
        setYear(value);

        if (validateDay(day) && validateMonth(month) && value) {
          setShowInvalidDateAlert(false);

          onSelect(`${value}-${day}-${month}`);
        } else setShowInvalidDateAlert(true);
      } else setShowInvalidDateAlert(true);
    } else {
      setShowInvalidDateAlert(false);

      if (value.length === 0) {
        // document.getElementById("month-input")?.focus();
        if (monthRef.current) monthRef.current.focus();
      }
    }
  };

  useEffect(() => {
    if (validateDay(day) && validateMonth(month) && validateYear(year))
      setIsDateValid(true);
    return () => {
      setIsDateValid(false);
    };
  }, [day, month, validateDay, validateMonth, validateYear, year]);

  return (
    <div className="w-full">
      {placeholder && (
        <label
          className="mb-[6px] block text-sm font-medium opacity-80"
          htmlFor="day-input"
        >
          {placeholder}
        </label>
      )}
      <div className="relative w-full">
        <div
          className={`flex h-[58px] w-full rounded-lg border-[1.5px] px-4 py-[5px] text-sm font-medium opacity-80 placeholder:text-grey focus-within:ring-1 border-primary ${
            !isDateValid ? "bg-white" : "bg-[#EAF6F8]"
          }  focus-within:border-primary focus-within:ring-primary`}
        >
          <input
            name="dob"
            aria-label="Date de naissance"
            maxLength={2}
            size={2}
            className="w-0 flex-1 bg-inherit text-center text-sm placeholder:text-neutral-400 focus:outline-none focus:outline-0 ring-0 border-0 focus:ring-0"
            pattern="[0-9]*"
            type="number"
            inputMode="numeric"
            placeholder="DD"
            min={rules?.day?.min?.toString() ?? "1"}
            max={rules?.day?.max?.toString() ?? "31"}
            step="1"
            value={day}
            onChange={handleDayChange}
            // id="day-input"
            ref={dayRef}
          />
          <span className="pb-2">.</span>
          <input
            maxLength={2}
            size={2}
            className="w-0 flex-1 bg-inherit text-center text-sm placeholder:text-neutral-400 focus:outline-none focus:outline-0 ring-0 border-0 focus:ring-0"
            pattern="[0-9]*"
            type="number"
            inputMode="numeric"
            placeholder="MM"
            min={rules?.month?.min?.toString() ?? "1"}
            max={rules?.month?.max?.toString() ?? "12"}
            step="1"
            value={month}
            onChange={handleMonthChange}
            // id="month-input"
            ref={monthRef}
          />
          <span className="pb-2">.</span>
          <input
            maxLength={4}
            size={4}
            className="w-0 flex-1 bg-inherit text-center text-sm placeholder:text-neutral-400 focus:outline-none focus:outline-0 ring-0 border-0 focus:ring-0"
            pattern="[0-9]*"
            type="number"
            inputMode="numeric"
            placeholder="YYYY"
            min={rules?.year?.min?.toString() ?? "1900"}
            max={
              rules?.year?.max?.toString() ?? undefined
              // moment().get("year").toString()
            }
            step="1"
            value={year}
            onChange={handleYearChange}
            // id="year-input"
            ref={yearRef}
          />
        </div>
      </div>
      {showInvalidDateAlert && (
        <span className="mt-1 block text-sm text-red-500">
          {errorMessage ?? "The date cannot be in the past"}
        </span>
      )}
    </div>
  );
};

export default QuestionDateInput;
